import { useEffect, useRef, useState, type FormEvent } from 'react'
import { authApi, describeError } from '@/lib/auth'
import { useAuthStore } from '@/store/auth'
import { Modal } from './Modal'
import styles from '@/pages/AuthPage.module.css'

export function UsernameModal({ current, onClose }: { current: string; onClose: () => void }) {
  const setUser = useAuthStore((s) => s.setUser)
  const [value, setValue] = useState(current)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)
  const [trigger] = useState(() => document.activeElement)
  const alive = useRef(true)
  const normalized = value.trim().replace(/^@/, '').toLowerCase()

  useEffect(() => {
    alive.current = true
    input.current?.focus()
    const dialog = input.current?.closest<HTMLElement>('[role="dialog"]')
    const trap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !dialog) return
      const controls = Array.from(dialog.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), [tabindex="0"]'))
      const first = controls[0]
      const last = controls.at(-1)
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus() }
    }
    dialog?.addEventListener('keydown', trap)
    return () => {
      alive.current = false
      dialog?.removeEventListener('keydown', trap)
      if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus()
    }
  }, [trigger])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy || !normalized || normalized === current) return
    setBusy(true)
    setError(null)
    try {
      const user = await authApi.changeUsername(normalized)
      // Ответ не должен обновить аккаунт, если за время запроса человек вышел или сменил его.
      if (useAuthStore.getState().user?.id === user.id) setUser(user)
      if (alive.current) onClose()
    } catch (err) {
      if (alive.current) {
        const detail = describeError(err)
        setError(detail.fields.username ?? detail.message)
      }
    } finally {
      if (alive.current) setBusy(false)
    }
  }

  return (
    <Modal title="Изменить имя пользователя" onClose={onClose}>
      <form className={styles.form} onSubmit={(e) => void submit(e)} noValidate aria-busy={busy}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="new-username">Имя после @</label>
          <input ref={input} id="new-username" className={styles.input} value={value} onChange={(e) => { setValue(e.target.value); setError(null) }} maxLength={33} autoFocus autoComplete="off" autoCapitalize="none" spellCheck={false} readOnly={busy} aria-invalid={!!error} aria-describedby={error ? 'username-hint username-cooldown username-error' : 'username-hint username-cooldown'} />
          <span id="username-hint" className={styles.hint}>2–32 символа: латиница, цифры, точка и подчёркивание.</span>
          <span id="username-cooldown" className={styles.hint}>Имя пользователя можно менять раз в сутки.</span>
        </div>
        {error ? <p id="username-error" className={styles.error} role="alert">{error}</p> : null}
        <button type="submit" className={styles.primary} disabled={busy || !normalized || normalized === current}>{busy ? 'Сохраняем…' : 'Сохранить'}</button>
      </form>
    </Modal>
  )
}
